import { slideContainer } from "./selectors"
import fullscreenAPI from "../utils/fullscreen-api"
import store from "./player-store"
import logEvent from "../tracking/amplitude/log-event"

let zoomIndex = 0
let logPinchZoomEvent = true
const ZOOM_LEVELS = [1, 1.2, 1.5, 1.75, 2, 2.5, 3, 4]
// minimum zoom 100%
const MIN_ZOOM_ALLOWED = 1
// maximum zoom 400%
const MAX_ZOOM_ALLOWED = 4
/**
 * use JS here instead of jQuery, event properties are not the same
 * causing this functionality to not work.
 */
const elementToPanAndZoom = document.getElementById("slide-container")

/**
 * Some Browsers expose transformation matrices
 * on the `WebKitCSSMatrix` interface instead of `DOMMatrix`
 */
if (!window.DOMMatrix) {
  if (window.WebKitCSSMatrix) {
    window.DOMMatrix = window.WebKitCSSMatrix
  }
}
store.initDOMMatrix = new DOMMatrix()
store.gesture = null

export const resetZoom = () => {
  store.zoom = 1
  slideContainer.css("transform", "")
}

const setPlayerZoom = () => {
  if (store.zoom > 1) {
    slideContainer.css("transform", `scale(${store.zoom})`)
  } else {
    resetZoom()
  }
}

export const updateZoom = (index) => {
  zoomIndex = index
  const zoom = ZOOM_LEVELS[index]
  store.zoom = zoom
  setPlayerZoom()
}

const zoomIn = () => {
  const newZoomIndex = zoomIndex + 1
  if (newZoomIndex === ZOOM_LEVELS.length) {
    return
  }
  updateZoom(newZoomIndex)
}

const zoomOut = () => {
  if (zoomIndex === 0) {
    return
  }
  const newZoomIndex = zoomIndex - 1
  updateZoom(newZoomIndex)
}

const distanceBetweenTouchPoints = (touches) => {
  const [t1, t2] = touches
  return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)
}

const midpoint = (touches) => {
  const [t1, t2] = touches
  return {
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  }
}

const getOrigin = (el, gestures) => {
  const rect = el.getBoundingClientRect()
  return {
    x: gestures.origin.x - rect.x,
    y: gestures.origin.y - rect.y,
  }
}

const getScaleFromMatrix = (matrix) => Math.hypot(matrix.a, matrix.b)

const clampScale = (gestured, matrix) => {
  const scale = getScaleFromMatrix(matrix)
  const proposedScale = gestured.scale * scale
  if (proposedScale > MAX_ZOOM_ALLOWED || proposedScale < MIN_ZOOM_ALLOWED) {
    return {
      ...gestured,
      scale: Math.max(MIN_ZOOM_ALLOWED, Math.min(MAX_ZOOM_ALLOWED, proposedScale)) / scale,
    }
  }
  return gestured
}

const applyMatrix = (el, matrix) => {
  el.style.transform = matrix
}

const gestureToMatrix = (gestures, curOrigin) => {
  return new DOMMatrix()
    .translate(curOrigin.x, curOrigin.y)
    .translate(gestures.translation.x || 0, gestures.translation.y || 0)
    .scale(gestures.scale || 1)
    .translate(-curOrigin.x, -curOrigin.y)
}

const startGesture = (gestured) => {
  /*
    Clear the element's transform so we can 
    measure its original position wrt. the screen.

    (We don't need to restore it because it gets 
    overwritten by `applyMatrix()` anyways.)
    */
  elementToPanAndZoom.style.transform = ""
  const gestureWithLimit = clampScale(gestured, store.initDOMMatrix)
  store.origin = getOrigin(elementToPanAndZoom, gestureWithLimit)
  applyMatrix(elementToPanAndZoom, gestureToMatrix(gestureWithLimit, store.origin).multiply(store.initDOMMatrix))
}

const doGesture = (gestured) => {
  const gestureWithLimit = clampScale(gestured, store.initDOMMatrix)
  applyMatrix(elementToPanAndZoom, gestureToMatrix(gestureWithLimit, store.origin).multiply(store.initDOMMatrix))
}

const endGesture = (gestured) => {
  const gestureWithLimit = clampScale(gestured, store.initDOMMatrix)
  store.initDOMMatrix = gestureToMatrix(gestureWithLimit, store.origin).multiply(store.initDOMMatrix)
  applyMatrix(elementToPanAndZoom, store.initDOMMatrix)
}

const touchMove = (e) => {
  if (e.touches.length === 2) {
    const mpInit = midpoint(store.initialTouches)
    const mpCurr = midpoint(e.touches)

    store.gesture = {
      scale: distanceBetweenTouchPoints(e.touches) / distanceBetweenTouchPoints(store.initialTouches),
      translation: {
        x: mpCurr.x - mpInit.x,
        y: mpCurr.y - mpInit.y,
      },
      origin: mpInit,
    }

    doGesture(store.gesture)
    e.preventDefault()
  }
}

const watchTouches = (e) => {
  if (fullscreenAPI.isFullscreen) {
    if (e.touches && e.touches.length === 2) {
      store.initialTouches = e.touches
      store.gesture = {
        scale: 1,
        translation: { x: 0, y: 0 },
        origin: midpoint(store.initialTouches),
      }
      if (e.type === "touchstart") {
        e.preventDefault()
        /**
         * disables browser pan & zoom actions while we
         * overrride pan + zoom, prevents accidental swipe (player navigation)
         * while panning
         */
        elementToPanAndZoom.style.touchAction = "none"
      }

      /*
        HTML elements have their `transform-origin` 
        set to '50% 50%' by default, we need to 
        reset it to the top-left corner.
      */
      elementToPanAndZoom.style.transformOrigin = "0 0"

      startGesture(store.gesture)
      elementToPanAndZoom.addEventListener("touchmove", touchMove, { passive: false })
      elementToPanAndZoom.addEventListener("touchend", watchTouches)
      elementToPanAndZoom.addEventListener("touchcancel", watchTouches)
    } else if (store.gesture) {
      endGesture(store.gesture)
      store.gesture = null
    }

    if (e.type === "touchend" || e.type === "touchcancel") {
      elementToPanAndZoom.style.touchAction = "auto"

      if (logPinchZoomEvent) {
        logEvent("slideshow_fullscreen_pinch_pan_zoom")
        logPinchZoomEvent = false
      }
    }
  }
}

export const initializeZoom = () => {
  store.zoom = 1
  $(".zoom-in").on("click", function (e) {
    e.preventDefault()
    e.stopPropagation()
    zoomIn()
  })

  $(".zoom-out").on("click", function (e) {
    e.preventDefault()
    e.stopPropagation()
    zoomOut()
  })

  // this is for sanity check
  if (elementToPanAndZoom instanceof HTMLElement) {
    elementToPanAndZoom.addEventListener("touchstart", watchTouches)
  }
}
